import { useState, useMemo } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { alpha, useTheme } from "@mui/material/styles";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSIconButton from "views/system-design/DSIconButton";
import DSTooltip from "views/system-design/DSTooltip";
import DSChip from "views/system-design/DSChip";
import DSAvatar from "views/system-design/DSAvatar";
import AddIcon from "@mui/icons-material/Add";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTickets } from "../../context/TicketsContext";
import { Ticket, REPO_LABELS, LABEL_COLORS } from "../../types";
import useUsers from "components/hooks/useUsers";
import { useLocalTicketOrder } from "../../hooks/useLocalTicketOrder";
import { useTicketSearch } from "../../hooks/useTicketQueries";
import { getInitials, getAvatarColor, resolveUserName } from "../../utils";
import KlydeaCard from "./KlydeaCard";
import CreateKlydeaModal from "./CreateKlydeaModal";
import KlydeaDetailModal from "./KlydeaDetailModal";

type ViewMode = "grid" | "list";

export default function KlydeasView() {
  const { updateTicket, deleteTicket } = useTickets();
  const { data: allUsers } = useUsers();
  const theme = useTheme();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const { sortByLocalOrder, calculateAndSetOrder } = useLocalTicketOrder();

  const handleToggleLabel = (label: string) => {
    setSelectedLabels((prev) =>
      prev.includes(label)
        ? prev.filter((existing) => existing !== label)
        : [...prev, label],
    );
  };

  // Ideas are their own server-side query: the board never loads them.
  const { tickets } = useTicketSearch(
    useMemo(
      () => ({
        filters: {
          statuses: ["idea" as const],
          isArchived: false,
          labelConditions:
            selectedLabels.length > 0 ? [{ anyOf: selectedLabels }] : undefined,
        },
      }),
      [selectedLabels],
    ),
  );

  const ideaTickets = useMemo(
    () => sortByLocalOrder(tickets),
    [tickets, sortByLocalOrder],
  );

  const handleTitleUpdate = (ticketId: string, title: string) => {
    updateTicket(ticketId, { title }, { silent: true });
  };

  const handleDelete = async (ticketId: string) => {
    await deleteTicket(ticketId);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.index === destination.index) return;

    const reordered = Array.from(ideaTickets);
    const [moved] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, moved);

    const prevTicket =
      destination.index > 0 ? reordered[destination.index - 1] : null;
    const nextTicket =
      destination.index < reordered.length - 1
        ? reordered[destination.index + 1]
        : null;

    calculateAndSetOrder(draggableId, prevTicket, nextTicket);
  };

  return (
    <DSBox
      sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LightbulbIcon sx={{ color: "info.main" }} />
          <DSTypography variant="h6" sx={{ fontWeight: 700 }}>
            Klydeas
          </DSTypography>
          <DSTypography variant="body2" sx={{ color: "text.secondary", ml: 1 }}>
            {ideaTickets.length} idea{ideaTickets.length !== 1 ? "s" : ""}
          </DSTypography>
        </DSBox>
        <DSBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <DSTooltip title="Grid view">
            <DSIconButton
              size="small"
              aria-label="Grid view"
              aria-pressed={viewMode === "grid"}
              onClick={() => setViewMode("grid")}
              sx={{
                color: viewMode === "grid" ? "info.main" : "text.disabled",
                bgcolor:
                  viewMode === "grid"
                    ? alpha(theme.palette.info.main, 0.1)
                    : "transparent",
              }}
            >
              <GridViewIcon fontSize="small" />
            </DSIconButton>
          </DSTooltip>
          <DSTooltip title="List view">
            <DSIconButton
              size="small"
              aria-label="List view"
              aria-pressed={viewMode === "list"}
              onClick={() => setViewMode("list")}
              sx={{
                color: viewMode === "list" ? "info.main" : "text.disabled",
                bgcolor:
                  viewMode === "list"
                    ? alpha(theme.palette.info.main, 0.1)
                    : "transparent",
              }}
            >
              <ViewListIcon fontSize="small" />
            </DSIconButton>
          </DSTooltip>
          <DSTooltip title="New Klydea">
            <DSIconButton
              onClick={() => setShowCreateModal(true)}
              sx={{
                bgcolor: "info.main",
                color: "common.white",
                "&:hover": { bgcolor: "info.dark" },
                boxShadow: 2,
              }}
            >
              <AddIcon />
            </DSIconButton>
          </DSTooltip>
        </DSBox>
      </DSBox>

      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        {REPO_LABELS.map((label) => {
          const isSelected = selectedLabels.includes(label);
          return (
            <DSChip
              key={label}
              label={label}
              size="small"
              variant={isSelected ? "filled" : "outlined"}
              onClick={() => handleToggleLabel(label)}
              sx={{
                cursor: "pointer",
                ...(isSelected && {
                  bgcolor: LABEL_COLORS[label] ?? "grey.500",
                  color: "common.white",
                  fontWeight: 600,
                }),
              }}
            />
          );
        })}
        {selectedLabels.length > 0 && (
          <DSChip
            label="Clear"
            size="small"
            variant="outlined"
            onClick={() => setSelectedLabels([])}
            sx={{ cursor: "pointer", fontStyle: "italic" }}
          />
        )}
      </DSBox>

      <DSBox
        sx={{
          flex: 1,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          bgcolor: "white.5",
          overflowY: "auto",
          p: 2,
        }}
      >
        {ideaTickets.length === 0 ? (
          <DSBox
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
            }}
          >
            <LightbulbIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <DSTypography variant="body1" sx={{ color: "text.secondary" }}>
              No klydeas yet
            </DSTypography>
            <DSTypography variant="caption" sx={{ color: "text.disabled" }}>
              Click + to add your first idea
            </DSTypography>
          </DSBox>
        ) : viewMode === "grid" ? (
          <DSBox
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
              maxWidth: "calc(7 * 220px)",
              gap: 2,
            }}
          >
            {ideaTickets.map((ticket) => (
              <KlydeaCard
                key={ticket.id}
                ticket={ticket}
                onClick={() => setSelectedTicket(ticket)}
                onTitleUpdate={handleTitleUpdate}
                onDelete={handleDelete}
                users={allUsers ?? []}
              />
            ))}
          </DSBox>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="klydeas-list">
              {(provided) => (
                <DSBox
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}
                >
                  {ideaTickets.map((ticket, index) => (
                    <Draggable
                      key={ticket.id}
                      draggableId={ticket.id}
                      index={index}
                    >
                      {(draggableProvided, snapshot) => (
                        <DSBox
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          style={draggableProvided.draggableProps.style}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedTicket(ticket)}
                          onKeyDown={(event) => {
                            if (
                              event.key === "Enter" ||
                              event.key === " " ||
                              event.key === "Spacebar"
                            ) {
                              event.preventDefault();
                              setSelectedTicket(ticket);
                            }
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            p: 1.25,
                            borderRadius: 1.5,
                            bgcolor: snapshot.isDragging
                              ? alpha(theme.palette.info.light, 0.2)
                              : "background.paper",
                            border: 1,
                            borderColor: snapshot.isDragging
                              ? "info.light"
                              : "divider",
                            borderLeft: 4,
                            borderLeftColor: "info.light",
                            cursor: "pointer",
                            boxShadow: snapshot.isDragging ? 4 : 1,
                            transition: "box-shadow 0.15s",
                            "&:hover": { boxShadow: 3 },
                            "&:hover .klydea-list-delete": {
                              opacity: 1,
                              pointerEvents: "auto",
                            },
                          }}
                        >
                          <DSBox
                            {...draggableProvided.dragHandleProps}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              color: "text.disabled",
                              cursor: "grab",
                              flexShrink: 0,
                              "&:active": { cursor: "grabbing" },
                            }}
                          >
                            <DragIndicatorIcon fontSize="small" />
                          </DSBox>

                          <DSTypography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {ticket.title}
                          </DSTypography>

                          {ticket.labels.length > 0 && (
                            <DSBox
                              sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}
                            >
                              {ticket.labels.slice(0, 3).map((label) => (
                                <DSChip
                                  key={label}
                                  label={label}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: 10,
                                    fontWeight: 700,
                                    bgcolor: LABEL_COLORS[label] ?? "grey.500",
                                    color: "common.white",
                                  }}
                                />
                              ))}
                              {ticket.labels.length > 3 && (
                                <DSTypography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    alignSelf: "center",
                                  }}
                                >
                                  +{ticket.labels.length - 3}
                                </DSTypography>
                              )}
                            </DSBox>
                          )}

                          {ticket.assignee &&
                            (() => {
                              const assigneeName = resolveUserName(
                                ticket.assignee,
                                allUsers ?? [],
                              );
                              return (
                                <DSTooltip title={assigneeName}>
                                  <DSAvatar
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      fontSize: 10,
                                      fontWeight: 600,
                                      flexShrink: 0,
                                      bgcolor: getAvatarColor(assigneeName),
                                    }}
                                  >
                                    {getInitials(assigneeName)}
                                  </DSAvatar>
                                </DSTooltip>
                              );
                            })()}

                          {ticket.comments.length > 0 && (
                            <DSBox
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.25,
                                color: "text.secondary",
                                flexShrink: 0,
                              }}
                            >
                              <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
                              <DSTypography
                                variant="caption"
                                sx={{ fontSize: 11 }}
                              >
                                {ticket.comments.length}
                              </DSTypography>
                            </DSBox>
                          )}

                          <DSIconButton
                            className="klydea-list-delete"
                            size="small"
                            aria-label="Delete idea"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                !window.confirm(
                                  "Are you sure you want to delete this idea?",
                                )
                              )
                                return;
                              void handleDelete(ticket.id);
                            }}
                            sx={{
                              opacity: 0,
                              pointerEvents: "none",
                              transition: "opacity 0.15s",
                              color: "error.main",
                              flexShrink: 0,
                              "&:hover": {
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                              },
                              "&:focus-visible": {
                                opacity: 1,
                                pointerEvents: "auto",
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </DSIconButton>
                        </DSBox>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </DSBox>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </DSBox>

      {selectedTicket && (
        <KlydeaDetailModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
        />
      )}

      {showCreateModal && (
        <CreateKlydeaModal onClose={() => setShowCreateModal(false)} />
      )}
    </DSBox>
  );
}
